import { useState } from 'react'

import { useFieldValues } from '../api/queries'

/**
 * A filter input that suggests values which actually occurred.
 *
 * Autocomplete over the data, not over a remembered list: a hunt screen that accepts
 * a hostname which has never appeared sends an analyst looking for an absence of
 * results that means nothing.
 *
 * A native `<datalist>` rather than a custom listbox. It is keyboard-operable and
 * screen-reader-announced in every browser this ships to without any of it being
 * reimplemented here, and a hand-rolled combobox is where console accessibility
 * usually goes to die.
 */
export function Suggested({
  label,
  field,
  value,
  tenant,
  onChange,
}: {
  label: string
  field: string
  value: string
  tenant?: string
  onChange: (value: string) => void
}) {
  const [prefix, setPrefix] = useState('')
  const suggestions = useFieldValues(field, prefix, tenant)
  const id = `suggest-${field}`

  return (
    <label>
      <span className="muted">{label}</span>
      <input
        aria-label={label}
        list={id}
        placeholder={field}
        value={value}
        onChange={(event) => {
          onChange(event.target.value)
          setPrefix(event.target.value)
        }}
      />
      {/* A failed suggestion query is silent on purpose: suggestions are a
          convenience, and an error message where somebody is typing is not. */}
      <datalist id={id}>
        {(suggestions.data?.values ?? []).map((entry) => (
          <option key={entry.value} value={entry.value}>
            {entry.count.toLocaleString()} events
          </option>
        ))}
      </datalist>
    </label>
  )
}
